import { createStore } from "zustand/vanilla";
import { extractUrls } from "@pack-it-up/core";
import { errorToMessage } from "../errorToMessage.js";
export const createNotesStore = (deps) => {
    // Stream subscriptions are owned by the store: dispose() unsubscribes them all, so a
    // superseded store stops writing into state nothing renders. `subs` is filled after
    // the store exists (the streams push into it).
    const subs = [];
    const store = createStore((set, get) => ({
        notes: [],
        error: null,
        add: async (body, tags) => {
            const r = await deps.notes.upsertNote({
                listId: deps.listId,
                body,
                links: extractUrls(body).map((url) => ({ url })),
                ...(tags !== undefined && tags.length > 0 ? { tags } : {}),
                authorId: deps.actor,
            });
            if (!r.ok)
                set({ error: errorToMessage(r.error) });
            return r;
        },
        // Every edit rebuilds the whole NoteWrite, so each one has to carry the fields it
        // isn't changing — otherwise editing a body would wipe the note's tags. Links are
        // the exception: they're re-derived from the new body every time.
        updateBody: async (noteId, body) => {
            const existing = get().notes.find((n) => n.id === noteId);
            const r = await deps.notes.upsertNote({
                id: noteId,
                listId: deps.listId,
                body,
                links: extractUrls(body).map((url) => ({ url })),
                ...(existing?.tags !== undefined ? { tags: existing.tags } : {}),
                authorId: existing?.authorId ?? deps.actor,
            });
            if (!r.ok)
                set({ error: errorToMessage(r.error) });
            return r;
        },
        updateLinks: async (noteId, links) => {
            const existing = get().notes.find((n) => n.id === noteId);
            const r = await deps.notes.upsertNote({
                id: noteId,
                listId: deps.listId,
                body: existing?.body ?? "",
                links,
                ...(existing?.tags !== undefined ? { tags: existing.tags } : {}),
                authorId: existing?.authorId ?? deps.actor,
            });
            if (!r.ok)
                set({ error: errorToMessage(r.error) });
            return r;
        },
        updateTags: async (noteId, tags) => {
            const existing = get().notes.find((n) => n.id === noteId);
            const r = await deps.notes.upsertNote({
                id: noteId,
                listId: deps.listId,
                body: existing?.body ?? "",
                links: existing?.links ?? [],
                ...(tags.length > 0 ? { tags } : {}),
                authorId: existing?.authorId ?? deps.actor,
            });
            if (!r.ok)
                set({ error: errorToMessage(r.error) });
            return r;
        },
        // optimistic, same-tick: a drop doesn't wait on the stream to round-trip before
        // the card settles into place.
        move: async (noteId, order) => {
            set({ notes: get().notes.map((n) => (n.id === noteId ? { ...n, order } : n)) });
            const r = await deps.notes.moveNote(noteId, order);
            if (!r.ok)
                set({ error: errorToMessage(r.error) });
            return r;
        },
        remove: async (noteId) => {
            const r = await deps.notes.deleteNote(noteId);
            if (!r.ok)
                set({ error: errorToMessage(r.error) });
            return r;
        },
        dispose() {
            for (const unsubscribe of subs.splice(0))
                unsubscribe();
        },
    }));
    subs.push(deps.notes.observeNotes(deps.listId).subscribe((notes) => store.setState({ notes })));
    return store;
};
//# sourceMappingURL=notesStore.js.map